import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { GraduationCap, User, Lock, AlertCircle } from 'lucide-react';
import { isMobileContainer } from '../utils/device';
import { openLandingInSystemBrowser } from '../utils/publicWeb';
import { getPostLoginPath } from '../utils/rolePaths';

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNativeMobile = isMobileContainer();

  useEffect(() => {
    const sso = searchParams.get('sso');
    const ssoMsg = searchParams.get('sso_msg');
    if (sso === 'missing') {
      setError('Single sign-on link was invalid. Please contact the administrator of CP Rawat Software Services.');
    } else if (sso === 'error') {
      setError(ssoMsg || 'Single sign-on failed or the link expired. Please contact the administrator of CP Rawat Software Services.');
    } else {
      try {
        const flash = sessionStorage.getItem('schoolerp:login_flash');
        if (flash) {
          sessionStorage.removeItem('schoolerp:login_flash');
          setError(flash);
        }
      } catch {
        /* ignore */
      }
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(identifier, password);

    if (result.success) {
      navigate(getPostLoginPath(result.user?.role_slug, result.user));
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div
      className={`min-h-screen flex flex-col bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 ${
        isNativeMobile ? 'pt-safe pb-safe px-4 justify-center' : 'items-center justify-center p-4'
      }`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent pointer-events-none" />

      {isNativeMobile && (
        <div className="relative z-10 text-center mb-6 px-2">
          <div className="mx-auto mb-4 w-20 h-20 bg-blue-600 rounded-3xl flex items-center justify-center shadow-xl shadow-blue-900/50">
            <GraduationCap className="w-11 h-11 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">School Analytics</h1>
          <p className="text-blue-300/90 text-sm font-medium mt-1">by CPRAWAT</p>
        </div>
      )}

      <Card
        className={`w-full relative z-10 bg-card/90 backdrop-blur-xl border-border/60 shadow-2xl ${
          isNativeMobile ? 'max-w-lg border-white/10' : 'max-w-md'
        }`}
      >
        <CardHeader className={`text-center ${isNativeMobile ? 'pb-1 pt-5' : 'pb-2'}`}>
          {!isNativeMobile && (
            <>
              <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <GraduationCap className="w-9 h-9 text-foreground" />
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">School ERP</CardTitle>
            </>
          )}
          {isNativeMobile ? (
            <CardTitle className="text-lg font-semibold text-foreground">Sign in to your account</CardTitle>
          ) : (
            <CardDescription className="text-muted-foreground">Sign in to manage your school</CardDescription>
          )}
          {isNativeMobile && (
            <CardDescription className="text-muted-foreground text-sm mt-1">
              Access your school dashboard on the go
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className={isNativeMobile ? 'px-5 pb-6' : undefined}>
          <form onSubmit={handleSubmit} className={`space-y-4 ${isNativeMobile ? 'space-y-5' : ''}`}>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-foreground">Email or mobile number</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="identifier"
                  type="text"
                  placeholder="Enter email or mobile number"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className={`pl-10 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground ${
                    isNativeMobile ? 'h-12 text-base' : ''
                  }`}
                  autoComplete="username"
                  required
                  data-testid="login-identifier-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-foreground">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-sky-400 hover:text-sky-300"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground ${
                    isNativeMobile ? 'h-12 text-base' : ''
                  }`}
                  autoComplete="current-password"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold ${
                isNativeMobile ? 'h-12 text-base rounded-xl shadow-lg shadow-blue-900/30' : 'py-5'
              }`}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground leading-relaxed">
            Need demo access? Use the{' '}
            {isNativeMobile ? (
              <button
                type="button"
                onClick={() => openLandingInSystemBrowser()}
                className="text-sky-400 hover:text-sky-300 underline underline-offset-2 font-medium align-baseline p-0 m-0 border-0 bg-transparent cursor-pointer inline"
                data-testid="login-open-landing-external"
              >
                enquiry form on our website
              </button>
            ) : (
              <Link
                to="/"
                className="text-sky-400 hover:text-sky-300 underline underline-offset-2 font-medium"
                data-testid="login-landing-link"
              >
                enquiry form on our website
              </Link>
            )}{' '}
            — we will email login details when approved.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
